import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTASection } from "@/components/CTASection";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { AUTOMACAO_LIST } from "@/lib/automacao-pages";
import { whatsappUrl } from "@/lib/site";

const TITLE = "Automação Comercial: Guia Completo para Empresas | SVG Multimídia";
const DESC =
  "Guia completo de automação comercial: o que é, o que pode ser automatizado, quanto custa, como escolher uma empresa e por onde começar para atender mais e vender melhor.";
const URL = "https://www.svgmultimidia.com.br/automacao-comercial";

const CLUSTER = [
  { t: "Como automatizar o atendimento da sua empresa", d: "O passo a passo para tirar o atendimento do improviso e colocar em um fluxo previsível." },
  { t: "Processos comerciais que podem ser automatizados", d: "O mapa do que vale automatizar primeiro — e o que deve continuar humano." },
  { t: "Como não perder leads", d: "As causas mais comuns de perda de oportunidade e como eliminá-las." },
  { t: "Como aumentar a produtividade comercial", d: "Quanto tempo o seu time perde com tarefas que uma automação resolve." },
  { t: "Automação de follow-up", d: "Cadências, intervalos e conteúdo dos retornos que realmente convertem." },
  { t: "IA no atendimento", d: "O que a inteligência artificial já faz bem e onde ela ainda precisa de gente." },
  { t: "Atendimento 24 horas", d: "Como manter a empresa disponível sem plantão e sem custo proporcional." },
  { t: "Indicadores de atendimento", d: "Quais métricas acompanhar para enxergar a saúde da operação." },
  { t: "Como escolher uma empresa de automação", d: "Critérios objetivos para não comprar ferramenta e ficar sem processo." },
  { t: "Quanto custa automatizar uma operação comercial", d: "Faixas de investimento, variáveis de preço e como calcular o retorno." },
];

const FAQ_ITEMS = [
  { q: "O que é automação comercial?", a: "É o uso de processos definidos, tecnologia e inteligência artificial para eliminar tarefas manuais do atendimento e das vendas — como responder dúvidas repetidas, registrar oportunidades, lembrar de follow-up e gerar relatórios." },
  { q: "Por onde começar?", a: "Pelo diagnóstico. Antes de contratar qualquer ferramenta, é preciso saber onde as oportunidades se perdem hoje: na velocidade de resposta, na qualificação, na proposta ou no acompanhamento." },
  { q: "Automação serve para empresa pequena?", a: "Sim. Operações pequenas costumam ter o ganho mais imediato, porque o tempo economizado volta direto para quem vende." },
  { q: "Quanto custa automatizar?", a: "O investimento varia com o número de canais, volume de contatos e nível de integração. Projetos enxutos começam com escopo reduzido e crescem por fases." },
  { q: "Em quanto tempo aparece resultado?", a: "Ganhos de tempo de resposta são percebidos nas primeiras semanas. Impactos em conversão costumam se consolidar entre 60 e 120 dias." },
  { q: "A automação substitui vendedores?", a: "Não. Ela remove o trabalho repetitivo e entrega ao vendedor oportunidades mais qualificadas e organizadas." },
];

export const Route = createFileRoute("/automacao-comercial")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Pillar,
});

function Pillar() {
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-16 md:pt-20 md:pb-20">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Automação comercial" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Guia completo</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              <span className="text-gradient">Automação comercial: guia completo para empresas</span>
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              Tudo o que sua empresa precisa entender para atender mais rápido, acompanhar todas as
              oportunidades e vender com previsibilidade — do diagnóstico à inteligência artificial.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={whatsappUrl()} target="_blank" rel="noopener" className="btn-primary">Solicitar diagnóstico</a>
              <Link to="/diagnostico-de-atendimento-e-vendas" className="btn-ghost">Ver diagnóstico comercial</Link>
            </div>
          </div>
        </div>
      </section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg">
          <p>
            Automação comercial é a combinação de <strong className="text-foreground">processo bem
            desenhado</strong>, <strong className="text-foreground">tecnologia adequada</strong> e{" "}
            <strong className="text-foreground">inteligência artificial</strong> para que nenhuma
            oportunidade dependa da memória de uma pessoa. Ela não substitui o time comercial: devolve
            tempo para que ele faça o que só gente faz bem — entender, convencer e fechar.
          </p>
          <p>
            Na prática, uma operação automatizada responde em segundos, qualifica o contato antes de
            ocupar um vendedor, registra tudo em um funil com etapas claras, dispara follow-up nos
            prazos certos e entrega ao gestor indicadores confiáveis de tempo de resposta e conversão.
          </p>
          <p>
            Este guia reúne as páginas de serviço da SVG Multimídia e os conteúdos que aprofundam cada
            parte do processo. Se você não sabe por onde começar, comece pelo diagnóstico: ele mostra,
            com números, onde sua empresa está perdendo dinheiro hoje.
          </p>
        </div>
      </Section>

      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader
          eyebrow="Serviços"
          title="Páginas de serviço em automação comercial"
          description="Escolha o serviço mais próximo da sua necessidade — todos podem ser combinados em um projeto único."
        />
        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {AUTOMACAO_LIST.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) as 0 | 1 | 2}>
              <Tilt3D>
                <Link to={"/" + p.slug} className="card-glass group block h-full transition-all duration-300 hover:-translate-y-1 hover:border-primary/40">
                  <h2 className="text-lg font-semibold transition-colors group-hover:text-primary">{p.navLabel}</h2>
                  <p className="mt-2 text-sm text-muted-foreground">{p.navDescription}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Ver página
                    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                  </span>
                </Link>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="Conteúdos relacionados"
          title="Temas que aprofundam o guia"
          description="Artigos do blog conectados a esta página principal — cada um detalha uma etapa da automação comercial."
        />
        <div className="mt-10 grid gap-3 md:grid-cols-2">
          {CLUSTER.map((c, i) => (
            <Reveal key={c.t} delay={(i % 3) as 0 | 1 | 2}>
              <div className="flex h-full flex-col rounded-2xl border border-ink/12 bg-secondary/40 p-5 shadow-[var(--shadow-soft)]">
                <h3 className="text-base font-semibold">{c.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/blog" className="btn-primary">Ler os artigos no blog</Link>
          <Link to="/automacao-comercial-para-empresas" className="btn-ghost">Página comercial da SVG</Link>
        </div>
      </Section>

      <CTASection
        title="Quer saber o que dá para automatizar na sua operação?"
        text="Fazemos um diagnóstico inicial do seu atendimento e mostramos onde estão as maiores perdas de oportunidade."
        buttonLabel="Quero meu diagnóstico"
      />

      <Section>
        <FAQ items={FAQ_ITEMS} />
      </Section>
    </>
  );
}
